import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider, useSuspenseQuery } from '@tanstack/react-query';
import { UIRouter, UIView } from '@uirouter/react';
import { FunctionComponent, Suspense } from 'react';
import { Provider } from 'react-redux';

import { AnonymousThreadProvider } from '@/ai-assistant/anonymous/AnonymousThreadProvider';
import { ThreadProvider } from '@/ai-assistant/logic/ThreadProvider';
import { ThreadRuntimeProvider } from '@/ai-assistant/logic/ThreadRuntimeProvider';
import { BootSplashGate } from '@/BootSplashGate';
import { BOOTSTRAP_QUERY_KEY, queryClient } from '@/core/queryClient';
import { DrawerProvider } from '@/drawer/DrawerContext';
import { DrawerRoot } from '@/drawer/DrawerRoot';
import { MatrixRoot } from '@/matrix/MatrixRoot';
import { ModalProvider } from '@/modal/ModalContext';
import { ModalRoot } from '@/modal/ModalRoot';
import { RealtimeRoot } from '@/realtime/RealtimeRoot';
import store from '@/store/store';

import { BootErrorScreen } from './BootErrorScreen';
import { loadConfig } from './core/bootstrap';
import { ErrorMessage } from './ErrorMessage';
import { ConfirmModalRoot } from './modal/ConfirmModalRoot';
import { RouteLoadingBar } from './navigation/RouteLoadingBar';
import { NotificationContainer } from './NotificationContainer';
import { router } from './router';
import { states } from './states';
import { ThemeProvider } from './theme/ThemeProvider';

states.forEach((state) => router.stateRegistry.register(state));

const ApplicationInner: FunctionComponent = () => {
  useSuspenseQuery({
    queryKey: BOOTSTRAP_QUERY_KEY,
    queryFn: loadConfig,
  });

  return (
    <ErrorBoundary fallback={ErrorMessage}>
      <UIRouter router={router}>
        <Provider store={store}>
          <ThemeProvider>
            {/* Drawer/Modal providers must wrap MatrixRoot: MatrixCallHost
                    (rendered inside MatrixRoot) opens the chat drawer from the
                    call widget via useDrawer, so it needs the same shared
                    DrawerProvider instance as the rest of the app. */}
            <ModalProvider>
              <DrawerProvider>
                <MatrixRoot>
                  <ThreadProvider>
                    <ThreadRuntimeProvider>
                      <AnonymousThreadProvider>
                        <RouteLoadingBar />
                        <RealtimeRoot />
                        <NotificationContainer />
                        <ModalRoot />
                        <ConfirmModalRoot />
                        <DrawerRoot />
                        <UIView />
                        <BootSplashGate />
                      </AnonymousThreadProvider>
                    </ThreadRuntimeProvider>
                  </ThreadProvider>
                </MatrixRoot>
              </DrawerProvider>
            </ModalProvider>
          </ThemeProvider>
        </Provider>
      </UIRouter>
    </ErrorBoundary>
  );
};

export const Application: FunctionComponent = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary
      fallback={({ error }) => <BootErrorScreen error={error as Error} />}
    >
      {/* No fallback of its own: the boot splash from index.html stays over
          the page until BootSplashGate removes it. */}
      <Suspense fallback={null}>
        <ApplicationInner />
      </Suspense>
    </ErrorBoundary>
  </QueryClientProvider>
);
